import React, { useCallback, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import useAPI from '../../../api';
import EntryListWrapper from '../../../Components/ContactListWrapper/EntriesWrapper/EntryListWrapper';
import ActivityTypeAccordion from './ActivityTypeAccordion';
import {
  ACTIVITY_TYPES_COLORS,
  ACTIVITY_TYPES_ICONS,
} from '../../../Utils/constants/activityTypes';
import { useAtomValue, useSetAtom } from 'jotai';
import { openSnackbarAdviseAtom, activityTypesAtom } from '../../../Atoms';
import removeEmptyKeys from '../../../Helpers/removeEmptyKeys';

const renderEntryComponent = ({
  data: { id, name, Icon },
  activityOutcomes,
  addToActivityOutcomes,
  onChangeIsActive,
  editActivityOutcome,
  activityTypes,
}) => {
  return (
    <ActivityTypeAccordion
      id={id}
      name={name}
      Icon={Icon}
      activityOutcomes={activityOutcomes}
      addToActivityOutcomes={addToActivityOutcomes}
      editActivityOutcome={editActivityOutcome}
      onChangeIsActive={onChangeIsActive}
      activityTypes={activityTypes}
    />
  );
};

const ActivityOutcomesConfiguration = function ActivityOutcomesConfiguration({
  reloadActivityOutcomes = () => {},
}) {
  const intl = useIntl();
  const API = useAPI();
  const openSnackbar = useSetAtom(openSnackbarAdviseAtom);

  const activityTypes = useAtomValue(activityTypesAtom);
  const [activityOutcomes, setActivityOutcomes] = useState([]);

  const loadActivityOutcomes = () =>
    API.getActivitiesOutcomes(
      (res) => {
        setActivityOutcomes(res);
      },
      () => {
        openSnackbar({
          message: intl.messages.ERROR_FETCHING_ACTIVITYOUTCOMES,
          severity: 'error',
        });
      }
    );

  useEffect(() => {
    if (activityTypes) loadActivityOutcomes();
  }, [activityTypes]);

  const handleAddToActivityOutcomes = (newActivityOutcome, onSuccess) => {
    const {
      name,
      activityTypeId,
      campaignAdvancement,
      removeCustomer,
      ...extensionData
    } = newActivityOutcome;

    const payload = {
      name,
      activityTypeId,
      campaignAdvancement: campaignAdvancement || 0,
      removeCustomer: removeCustomer || false,
      extensionData: JSON.stringify(removeEmptyKeys(extensionData)) || null,
    };

    API.addActivityOutcomes(
      payload,
      () => {
        loadActivityOutcomes();
        reloadActivityOutcomes();
        if (onSuccess) {
          onSuccess();
        }
        openSnackbar({
          message: intl.messages.ACTIVITYOUTCOME_ADDED_SUCCESSFULLY,
          severity: 'success',
        });
      },
      () => {
        openSnackbar({
          message: intl.messages.ERROR_ADDING_ACTIVITYOUTCOME,
          severity: 'error',
        });
      }
    );
  };

  const handleEditActivityOutcome = (updatedActivityOutcome, onSuccess) => {
    const {
      id,
      name,
      activityTypeId,
      campaignAdvancement,
      removeCustomer,
      ...extensionData
    } = updatedActivityOutcome;

    const payload = {
      id,
      name,
      activityTypeId,
      campaignAdvancement: campaignAdvancement || 0,
      removeCustomer: removeCustomer || false,
      extensionData: JSON.stringify(removeEmptyKeys(extensionData)) || null,
    };

    API.updateActivityOutcomes(
      payload,
      () => {
        loadActivityOutcomes();
        reloadActivityOutcomes();
        if (onSuccess) {
          onSuccess();
        }
        openSnackbar({
          message: intl.messages.ACTIVITYOUTCOME_EDITED_SUCCESSFULLY,
          severity: 'success',
        });
      },
      () => {
        openSnackbar({
          message: intl.messages.ERROR_EDITING_ACTIVITYOUTCOME,
          severity: 'error',
        });
      }
    );
  };

  const handleToggleIsActive = (id) => {
    API.activityOutcomesToggle(
      id,
      () => {
        setActivityOutcomes((prevState) =>
          prevState.map((outcome) =>
            outcome.id === id
              ? { ...outcome, isActive: !outcome.isActive }
              : outcome
          )
        );
        reloadActivityOutcomes();
      },
      () => {
        openSnackbar({
          message: intl.messages.ERROR_UPDATING_ACTIVITYOUTCOME,
          severity: 'error',
        });
      }
    );
  };
  const customEntryFunction = useCallback(
    (data) =>
      renderEntryComponent({
        data,
        activityOutcomes: activityOutcomes?.filter(
          ({ activityTypeId }) =>
            activityTypeId ===
            activityTypes.find(({ name }) => name === data.name)?.id
        ),
        addToActivityOutcomes: handleAddToActivityOutcomes,
        editActivityOutcome: handleEditActivityOutcome,
        onChangeIsActive: handleToggleIsActive,
        activityTypes,
      }),
    [activityOutcomes, activityTypes]
  );

  return (
    activityTypes && (
      <EntryListWrapper
        singlePageList={(activityTypes || [])
          .filter(({ name }) =>
            Object.keys(ACTIVITY_TYPES_COLORS)?.includes(name)
          )
          .map(({ id, name }) => ({
            id,
            name: name,
            Icon: ACTIVITY_TYPES_ICONS[name],
          }))}
        customEntryFunction={customEntryFunction}
      />
    )
  );
};

export default ActivityOutcomesConfiguration;
